<script setup lang="ts">
import type { Media, SlideshowConfig } from "@/models"
import { useFocusWithin, useFullscreen, useScrollLock, useTimeoutFn } from "@vueuse/core"
import { computed, nextTick, onBeforeUnmount, ref, toRef, useTemplateRef, watch, watchEffect } from "vue"
import { Icon } from "@/components/ui"
import { useSlideshow, useTranslation } from "@/composables"
import { useSlideshowMediaCache } from "@/composables/useSlideshowMediaCache"
import { MediaType } from "@/models"
import { safelyPlayVideo, safelyUnloadVideoElement } from "@/utils"
import SlideshowControls from "./SlideshowControls.vue"
import SlideshowTitle from "./SlideshowTitle.vue"

interface SlideshowProps {
  slideshowConfig: SlideshowConfig
  transitionEffect?: "slideshow-fade" | "slide" | "flip"
  autoplay?: boolean
  isLoading?: boolean
  enableTitleSlide?: boolean
  enableQrInTitleSlide?: boolean
  titleSlideDurationMs?: number
}

const {
  slideshowConfig,
  transitionEffect = "slideshow-fade",
  autoplay = false,
  isLoading = false,
} = defineProps<SlideshowProps>()

const slideshowWrapper = useTemplateRef("slideshowWrapper")
const slideshowBackground = useTemplateRef("slideshowBackground")

const { focused } = useFocusWithin(slideshowWrapper)

const { t } = useTranslation()

const isBodyLocked = useScrollLock(document.body)

const {
  isFullscreen,
  toggle: toggleFullscreen,
  exit: exitFullscreen,
} = useFullscreen(slideshowBackground, { autoExit: true })

const { pagination, media, hide, visible, state, play, pause, reset, handleMediaEnd } = useSlideshow(toRef(slideshowConfig.id ?? ""))

const currentMediaEl = ref<HTMLImageElement | HTMLVideoElement | null>(null)
const isMediaReady = ref<boolean>(false)
const { has: hasLoadedMedia, add: addLoadedMedia } = useSlideshowMediaCache()

const currentMedia = computed<Media | undefined>(() => {
  if (!media.value?.length) {
    return
  }

  return media.value[pagination.page.value - 1]
})

const showSlideshowControls = ref(false)
const { start: startTimeout, stop: clearTimeout } = useTimeoutFn(() => {
  showSlideshowControls.value = false
}, 1500)

function handleSlideshowMouseMove() {
  showSlideshowControls.value = true
  clearTimeout()
  startTimeout()
}

function handleFullscreen() {
  toggleFullscreen().catch(() => {})
}

function handleClose() {
  exitFullscreen().catch(() => {})
  reset()
  hide()
}

function handleTitleEnded() {
  if (autoplay) {
    play()
  }
}

watchEffect(() => {
  if (visible.value && slideshowConfig.enableTitleSlide && state.value === "IDLE") {
    state.value = "TITLE"
  }
})

watchEffect(() => {
  if (visible.value && state.value === "IDLE" && !slideshowConfig.enableTitleSlide && !isLoading && autoplay) {
    play()
  }
})

watchEffect(() => {
  isBodyLocked.value = Boolean(visible.value)
})

watch(
  [() => currentMedia.value?.id, () => state.value, () => visible.value],
  async ([_, currentState, isVisible]) => {
    if (!isVisible) {
      isMediaReady.value = false

      return
    }

    if (currentState === "TITLE") {
      isMediaReady.value = true

      return
    }

    isMediaReady.value = false
    await nextTick()

    const mediaEl = currentMediaEl.value

    if (!mediaEl) {
      return
    }

    if (mediaEl instanceof HTMLImageElement) {
      if (mediaEl.complete && mediaEl.naturalWidth > 0) {
        isMediaReady.value = true
      }

      return
    }

    if (mediaEl.readyState >= 2) {
      isMediaReady.value = true
    }
  },
  { immediate: true },
)

// Clean up the outgoing video element before it is swapped out for the next
// slide, or removed when the slideshow is closed. This runs before Vue
// re-renders (default "pre" watch flush), so `currentMediaEl.value` still
// points at the element about to be replaced/removed. See FeedTile.vue for
// the same iOS Safari `InvalidStateError` mitigation.
watch(() => currentMedia.value?.id, () => {
  safelyUnloadVideoElement(currentMediaEl.value instanceof HTMLVideoElement ? currentMediaEl.value : null)
})

// Videos rely on a JS-driven `play()` call (instead of the `autoplay`
// attribute) so a rejected play request can be caught instead of surfacing
// as an unhandled `InvalidStateError`/`NotAllowedError` on iOS. Watching the
// template ref itself (rather than `currentMedia.id` + `nextTick`) ensures
// this fires once Vue actually mounts the new element, even though the
// `out-in` transition on the slide wrapper delays that mount. If playback
// fails while the slideshow is actively auto-cycling, advance to the next
// slide instead of leaving it stuck on a slide that will never emit `ended`.
watch(currentMediaEl, (mediaEl) => {
  if (!(mediaEl instanceof HTMLVideoElement))
    return

  safelyPlayVideo(mediaEl, () => {
    // Only skip ahead if this is still the current slide's video - avoids
    // double-advancing if `useSlideshow().play()`'s own play attempt (for
    // the same element) already handled the failure.
    if (state.value === "PLAYING" && currentMediaEl.value === mediaEl)
      handleMediaEnd()
  })
})

onBeforeUnmount(() => {
  safelyUnloadVideoElement(currentMediaEl.value instanceof HTMLVideoElement ? currentMediaEl.value : null)
})

const showLoadingIcon = computed(() => {
  if (!visible.value) {
    return false
  }

  if (state.value === "TITLE") {
    return false
  }

  if (!currentMedia.value) {
    return true
  }

  const slideshowId = slideshowConfig.id ?? ""
  const mediaId = currentMedia.value.id

  if (slideshowId && mediaId !== undefined && hasLoadedMedia(slideshowId, String(mediaId), currentMedia.value.type)) {
    return false
  }

  return Boolean(!isMediaReady.value)
})

function markMediaReady() {
  isMediaReady.value = true

  if (currentMedia.value) {
    const slideshowId = slideshowConfig.id ?? ""
    const mediaId = currentMedia.value.id

    if (slideshowId && mediaId !== undefined) {
      addLoadedMedia(slideshowId, String(mediaId), currentMedia.value.type)
    }
  }
}

function hasAnyDetail(media: Media): boolean {
  return Boolean(media.title || media.description || media.uploadedBy)
}
</script>

<template>
  <div
    v-if="visible"
    ref="slideshowBackground"
    class="slideshow-background fixed inset-0 z-50"
    data-test="slideshow-background"
  >
    <div
      v-if="showLoadingIcon"
      class="fixed inset-0 flex items-center justify-center z-50"
      data-test="slideshow-loading"
    >
      <Icon icon="Loading" size="xl" class="text-white" />
      <span class="sr-only">Loading...</span>
    </div>
    <div>
      <div v-for="(m, i) in media" :key="i" class="opacity-0 h-0 w-0 hidden">
        <img v-if="m.type === MediaType.Pic" :src="m.fullSizeUrl" alt="">
        <video v-if="m.type === MediaType.Vid" :poster="m.thumbnailUrl">
          <source :src="m.fullSizeUrl">
        </video>
      </div>
      <Transition name="gpx-fade" mode="out-in">
        <SlideshowTitle
          v-if="slideshowConfig.enableTitleSlide && state === 'TITLE'"
          :slideshow-config="slideshowConfig"
          @play="handleTitleEnded"
        />
        <div
          v-else
          ref="slideshowWrapper"
          tabindex="0"
          data-test="control_wrapper"
          @mousemove="handleSlideshowMouseMove"
          @touchstart="handleSlideshowMouseMove"
        >
          <div class="fixed inset-0 z-50">
            <div v-if="currentMedia" class="text-white flex flex-col h-screen">
              <Transition :name="`gpx-${transitionEffect}`" mode="out-in">
                <div
                  :key="currentMedia.id"
                  class="flex-1 flex min-h-0 justify-center items-center"
                >
                  <video
                    v-if="currentMedia?.type === MediaType.Vid"
                    :id="`gpx-slideshow-video-${currentMedia?.id ?? 'unknown'}`"
                    ref="currentMediaEl"
                    playsinline
                    :poster="currentMedia?.thumbnailUrl"
                    class="max-w-full max-h-full h-full object-contain"
                    data-test="slideshow_current"
                    @loadeddata="markMediaReady"
                    @error="markMediaReady"
                  >
                    <source :src="currentMedia!.fullSizeUrl">
                  </video>
                  <img
                    v-else
                    ref="currentMediaEl"
                    :alt="currentMedia!.title"
                    :src="currentMedia!.fullSizeUrl"
                    class="max-w-full max-h-full h-full object-contain"
                    data-test="slideshow_current"
                    @load="markMediaReady"
                    @error="markMediaReady"
                  >
                </div>
              </Transition>
              <div
                v-if="slideshowConfig.enableDetails"
                v-show="hasAnyDetail(currentMedia)"
                class="w-full bg-black/50"
              >
                <Transition name="gpx-fade" mode="out-in">
                  <dl :key="currentMedia?.id" class="p-3 w-full text-center">
                    <dt v-if="currentMedia?.title" class="sr-only">
                      {{ t("slideshow.details_title") }}
                    </dt>
                    <dd
                      v-if="currentMedia?.title"
                      class="line-clamp-1 text-ellipsis"
                      data-test="details_title"
                    >
                      {{ currentMedia.title }}
                    </dd>
                    <dt v-if="currentMedia?.description" class="sr-only">
                      {{ t("slideshow.details_desc") }}
                    </dt>
                    <dd
                      v-if="currentMedia?.description"
                      class="text-sm line-clamp-1 text-ellipsis"
                      data-test="details_desc"
                    >
                      {{ currentMedia?.description }}
                    </dd>
                    <dt v-if="currentMedia?.uploadedBy" class="sr-only">
                      {{ t("slideshow.details_uploaded_by") }}
                    </dt>
                    <dd
                      v-if="currentMedia?.uploadedBy"
                      class="flex"
                      data-test="details_uploaded_by"
                    >
                      <Icon icon="Camera" class="mr-1.5" />{{
                        currentMedia?.uploadedBy
                      }}
                    </dd>
                  </dl>
                </Transition>
              </div>
            </div>
          </div>
          <Transition name="gpx-fade">
            <SlideshowControls
              v-show="showSlideshowControls || focused"
              :state="state"
              :is-fullscreen="isFullscreen"
              @play="play"
              @pause="pause"
              @next="pagination.next"
              @previous="pagination.previous"
              @fullscreen="handleFullscreen"
              @close="handleClose"
            />
          </Transition>
        </div>
      </Transition>
    </div>
  </div>
</template>

<style>
.slideshow-background {
  --slideshow-bg: v-bind("slideshowConfig.backgroundColour");

  background-color: var(--slideshow-bg, rgb(0 0 0));
}

.gpx-slideshow-fade-enter-active,
.gpx-slideshow-fade-leave-active {
  transition: opacity 0.75s ease;
}

.gpx-slideshow-fade-enter-from,
.gpx-slideshow-fade-leave-to {
  opacity: 0;
}

.gpx-slide-enter-active,
.gpx-slide-leave-active {
  transition: transform 0.75s ease;
}

.gpx-slide-enter-from {
  transform: translateX(100%);
}

.gpx-slide-leave-to {
  transform: translateX(-100%);
}

.gpx-flip-enter-active,
.gpx-flip-leave-active {
  transition: transform 0.75s ease;
}

.gpx-flip-enter-from {
  transform: rotateY(90deg);
}

.gpx-flip-leave-to {
  transform: rotateY(-90deg);
}
</style>
